import {AfterViewInit, Component, ElementRef, HostListener, Inject, inject, OnInit, ViewChild} from '@angular/core';
import {RestaurantDetailResponse} from "../../../../@domain/resturant-detail";
import {firstValueFrom, Observable} from "rxjs";
import {ApiEnum} from "../../../../@application/enum/api.enum";
import {ApiBaseService, StorageService} from '../../../../../../projects/vendors/src/public-api';
import {FoodItemQuantity} from "../../../../@domain/food-item-quantity";
import {FormArray, FormControl, FormGroup, Validators} from "@angular/forms";
import {NgxPrintService, PrintOptions} from "ngx-print";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {FormHelper, KeyValue} from "../../../../../../projects/vendors/src/lib/services/form-helper";
import {OrderType} from "../../../../@application/enum/order-type.enums";
import {PaymentMethods} from "../../../../@application/enum/payment-methods";
import {CartRequestDetails} from "../../../../@domain/cart-request-details";
import {RestaurantConfig} from "../../../../@domain/restaurant-config";
import {PaymentTotals} from "../../../../../../projects/vendors/src/lib/components/bill-printer-section/payment-totals.interface";

@Component({
    selector: 'app-pos-checkout-dialog',
    templateUrl: './pos-checkout-dialog.component.html',
    styleUrl: './pos-checkout-dialog.component.scss',
    standalone: false
})
export class PosCheckoutDialogComponent implements OnInit, AfterViewInit {

  @ViewChild('receiptWrapper') receiptWrapper!: ElementRef;
  @ViewChild('paidAmountInput') paidAmountInput!: ElementRef<HTMLInputElement>;

  restaurantDetail = {} as RestaurantConfig
  px2mmFactor!: number;
  private paidAmountString: string = '';
  billForm!: FormGroup;
  placeholder = 'Enter remark';
  isCard = false;
  cardTaxAdded: boolean = false;
  cardTax: number = 0;
  discountAdded: boolean = false;
  paidAdded: boolean = false;
  totalAmount: number = 0;
  discount: number = 0;
  paidAmount: number = 0;
  grandTotal: number = 0;
  totalPrice: number = 0;
  balance: number = 0;
  serviceCharge: number = 0;
  tax: number = 0;
  serviceChargeChecked: boolean = false;
  taxChecked = false;
  foodItemQuantity: FoodItemQuantity[] = []
  paymentMethodKeyValue = [] as KeyValue[]
  orderType = OrderType
  paymentMethod = PaymentMethods
  userName!: string
  date = new Date();
  serviceChargeableTotalAmount: number = 0;
  submitting = false;

  // 2.5in receipt printer instead of the 3in default - see docs/RECEIPT_WIDTH.md
  smallTenant = 'b6778fd2-1972-4683-9998-3a9f3fb65fb0'
  tenantId!: string
  shouldApplyWidth = false;
  private lastEnterPressTime = 0;
  private readonly DOUBLE_PRESS_THRESHOLD = 500; // milliseconds

  private apiBaseService = inject(ApiBaseService)
  private storageService = inject(StorageService)
  private printService = inject(NgxPrintService)

  constructor(@Inject(MAT_DIALOG_DATA) public data: {
                cartFoods: CartRequestDetails[],
                foods: any[],
                sectionId: string
              },
              private dialogRef: MatDialogRef<PosCheckoutDialogComponent>) {
    this.paymentMethodKeyValue = FormHelper.PaymentMethodsList()
    this.totalPrice = this.data.cartFoods.reduce((acc, curr) => acc + (curr.price * curr.quantity), 0)
  }

  async ngOnInit() {
    this.tenantId = this.storageService.getFromSessionStorage('tenantId');
    this.shouldApplyWidth = this.tenantId === this.smallTenant;
    this.px2mmFactor = this.calcPx2MmFactor();
    this.getFoodItemQuantity()
    this.formInitializer()
    // A failed config fetch must not leave the dialog half-initialised: without the
    // default payment method below the form stays invalid and Close Order does nothing.
    try {
      this.restaurantDetail = await firstValueFrom(this.getRestaurantData())
      this.billForm.get('orderBillRequest.taxDetail.taxPercentage')?.setValue(this.restaurantDetail.restaurantDetail.tax)
      this.billForm.get('orderBillRequest.serviceChargeDetail.serviceChargePercentage')?.setValue(this.restaurantDetail.restaurantDetail.serviceCharge)
    } catch {
      // Percentages stay null, which calculates as 0.
    }

    // Auto-select cash payment
    this.billForm.get('orderBillRequest.paymentMethod')?.setValue(1);

    this.calculateTotalAmount();
    this.updateCalculations();
    this.billForm.get('orderBillRequest.discountDetail.discountPercentage')?.valueChanges.subscribe(() => this.updateCalculations());
    this.billForm.get('orderBillRequest.paidAmount')?.valueChanges.subscribe(() => this.updateCalculations());
    this.billForm.get('orderBillRequest.paymentMethod')?.valueChanges
      .subscribe({
        next: (value) => {
          if (value === 1 || value === 3) {
            this.placeholder = 'Enter remark';
            this.isCard = false;
            this.removeCardTax();
          } else if (value === 2) {
            this.placeholder = 'Enter the last 4 digits of the card';
            this.isCard = true;
          } else if (value === 4) {
            this.placeholder = 'Enter payment method';
            this.isCard = false;
            this.removeCardTax();
          }
        }
      })

  }

  onPaidAmount() {
    this.paidAdded = true;
    this.updateCalculations();
  }

  private removeCardTax() {
    if (this.cardTaxAdded) {
      this.billForm.get('orderBillRequest.cardTax')?.setValue(0);
      this.cardTaxAdded = false;
      this.updateCalculations();
    }
  }

  onCardTax() {
    this.cardTaxAdded = this.billForm.get('orderBillRequest.cardTax')?.value > 0;
    this.updateCalculations();
  }

  ngAfterViewInit(): void {
    // Auto-focus on paid amount input
    setTimeout(() => {
      this.paidAmountInput?.nativeElement?.focus();
    }, 100);
  }

  selectPaymentMethod(method: number): void {
    this.billForm.get('orderBillRequest.paymentMethod')?.setValue(method);
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardNavigation(event: KeyboardEvent): void {
    // Handle double Enter key press to open print view
    if (event.key === 'Enter') {
      const currentTime = Date.now();
      if (currentTime - this.lastEnterPressTime < this.DOUBLE_PRESS_THRESHOLD) {
        event.preventDefault();
        this.openPrintView();
        this.lastEnterPressTime = 0; // Reset to avoid triple-press triggering again
        return;
      }
      this.lastEnterPressTime = currentTime;
      return;
    }

    // Only handle Shift + Arrow keys
    if (!event.shiftKey || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) {
      return;
    }

    // Don't interfere when user is typing in text areas or inputs (except paidAmount)
    const target = event.target as HTMLElement;
    if (target.tagName === 'TEXTAREA' ||
        (target.tagName === 'INPUT' && target !== this.paidAmountInput?.nativeElement)) {
      return;
    }

    event.preventDefault();

    const currentMethod = this.billForm.get('orderBillRequest.paymentMethod')?.value || 1;
    let newMethod: number;

    if (event.key === 'ArrowRight') {
      // Move to next payment method (1-6, wrap around)
      newMethod = currentMethod >= 6 ? 1 : currentMethod + 1;
    } else {
      // Move to previous payment method (1-6, wrap around)
      newMethod = currentMethod <= 1 ? 6 : currentMethod - 1;
    }

    this.selectPaymentMethod(newMethod);
  }

  openPrintView(): void {
    const customPrintOptions: PrintOptions = new PrintOptions({
      printSectionId: 'print-section',
      useExistingCss: true,
    });
    this.printService.print(customPrintOptions);
  }

  get paidAmountHasValue(): boolean {
    const value = this.billForm.get('orderBillRequest.paidAmount')?.value;
    return value !== null && value !== undefined && value !== '';
  }

  get formattedPaidAmount(): string {
    const value = this.billForm.get('orderBillRequest.paidAmount')?.value;
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const parts = String(value).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }

  onNumpadClick(value: string): void {
    if (value === '.' && this.paidAmountString.includes('.')) {
      return;
    }
    if (value === '.' && this.paidAmountString === '') {
      this.paidAmountString = '0.';
    } else {
      this.paidAmountString += value;
    }
    const numericValue = parseFloat(this.paidAmountString);
    if (!isNaN(numericValue)) {
      this.billForm.get('orderBillRequest.paidAmount')?.setValue(numericValue);
      this.onPaidAmount();
    }
  }

  onNumpadDelete(): void {
    if (this.paidAmountString.length > 0) {
      this.paidAmountString = this.paidAmountString.slice(0, -1);
      if (this.paidAmountString === '' || this.paidAmountString === '0') {
        this.billForm.get('orderBillRequest.paidAmount')?.setValue(null);
        this.paidAdded = false;
      } else {
        const numericValue = parseFloat(this.paidAmountString);
        this.billForm.get('orderBillRequest.paidAmount')?.setValue(numericValue);
      }
      this.updateCalculations();
    }
  }

  onKeyboardInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let rawValue = input.value.replace(/,/g, '').replace(/[^0-9.]/g, '');

    const decimalCount = (rawValue.match(/\./g) || []).length;
    if (decimalCount > 1) {
      const firstDecimalIndex = rawValue.indexOf('.');
      rawValue = rawValue.substring(0, firstDecimalIndex + 1) + rawValue.substring(firstDecimalIndex + 1).replace(/\./g, '');
    }

    if (rawValue === '' || rawValue === null) {
      this.paidAmountString = '';
      this.billForm.get('orderBillRequest.paidAmount')?.setValue(null);
      this.paidAdded = false;
    } else {
      const numericValue = parseFloat(rawValue);
      if (!isNaN(numericValue) && numericValue >= 0) {
        this.paidAmountString = rawValue;
        this.billForm.get('orderBillRequest.paidAmount')?.setValue(numericValue);
        this.onPaidAmount();
      }
    }
    this.updateCalculations();
  }

  onPaidAmountKeyPress(event: KeyboardEvent): boolean {
    const charCode = event.key;
    if (
      /^[0-9]$/.test(charCode) ||
      charCode === '.' ||
      charCode === 'Backspace' ||
      charCode === 'Delete' ||
      charCode === 'Tab' ||
      charCode === 'Escape' ||
      charCode === 'Enter' ||
      charCode === 'ArrowLeft' ||
      charCode === 'ArrowRight'
    ) {
      if (charCode === '.' && this.paidAmountString.includes('.')) {
        event.preventDefault();
        return false;
      }
      return true;
    }
    event.preventDefault();
    return false;
  }

  formInitializer() {
    this.userName = this.storageService.getFromSessionStorage('userDetails')?.givenName
    this.billForm = new FormGroup({
      sectionId: new FormControl(this.data.sectionId),
      customerName: new FormControl(null),
      customerPhone: new FormControl(null),
      cartRequestDetails: new FormArray([]),
      orderBillRequest: new FormGroup({
        orderId: new FormControl(null),
        paymentMethod: new FormControl(null, [Validators.required]),
        totalOrderAmount: new FormControl(this.totalPrice, [Validators.required]),
        paidAmount: new FormControl(null, [Validators.required, Validators.min(0)]),
        balance: new FormControl(null),
        cardTax: new FormControl(null),
        netAmount: new FormControl(null, [Validators.min(0)]),
        remarks: new FormControl(null),
        marginPercentage: new FormControl(null),
        discountDetail: new FormGroup({
          discountPercentage: new FormControl(null, [Validators.min(0),
            Validators.max(100)]),
          discountAmount: new FormControl(null, [Validators.min(0)]),
          amountBeforeDiscount: new FormControl(this.totalPrice),
          amountAfterDiscount: new FormControl(null),
        }),
        taxDetail: new FormGroup({
          taxPercentage: new FormControl(null),
          taxAmount: new FormControl(null),
          amountBeforeTax: new FormControl(this.totalPrice),
          amountAfterTax: new FormControl(null),
        }),
        serviceChargeDetail: new FormGroup({
          serviceChargePercentage: new FormControl(null),
          serviceChargeAmount: new FormControl(null),
          amountBeforeServiceCharge: new FormControl(this.totalPrice),
          amountAfterServiceCharge: new FormControl(null),
        })
      })
    });
    this.setCartRequestDetails()
  }

  get cartRequestDetails(): FormArray {
    return this.billForm.get('cartRequestDetails') as FormArray;
  }

  // Data structure for lib-bill-printer-section component
  get billPrinterData(): { onGoingOrder: any; foods: any[] } {
    return {
      onGoingOrder: {
        orderNumber: 'POS',
        orderType: OrderType.Pos,
        totalPrice: this.totalPrice,
        cartResponseDetails: this.data.cartFoods.map(item => ({
          foodId: item.foodId,
          foodQuantityTagId: item.foodQuantityTagId,
          quantity: item.quantity,
          totalPrice: item.price * item.quantity
        }))
      },
      foods: this.data.foods
    };
  }

  // Payment totals for lib-bill-printer-section component
  get paymentTotals(): PaymentTotals {
    return {
      discountAmount: this.discountAdded ? this.discount : undefined,
      serviceChargeAmount: this.serviceChargeChecked ? this.serviceCharge : undefined,
      serviceChargePercentage: this.serviceChargeChecked ? this.billForm.get('orderBillRequest.serviceChargeDetail.serviceChargePercentage')?.value : undefined,
      taxAmount: this.taxChecked ? this.tax : undefined,
      taxPercentage: this.taxChecked ? this.billForm.get('orderBillRequest.taxDetail.taxPercentage')?.value : undefined,
      paymentMethodValue: this.billForm.get('orderBillRequest.paymentMethod')?.value || 1,
      paidAmount: this.billForm.get('orderBillRequest.paidAmount')?.value || 0,
      grandTotal: this.grandTotal,
      balance: this.balance
    };
  }

  private setCartRequestDetails() {
    this.data.cartFoods.forEach(food => {
      const cartRequestDetails = new FormGroup({
        foodId: new FormControl(food.foodId),
        menuId: new FormControl(food.menuId),
        kitchenId: new FormControl(food.kitchenId),
        quantity: new FormControl(food.quantity),
        kitchenIntroduction: new FormControl(food.kitchenIntroduction),
        price: new FormControl(food.price),
        extras: new FormControl(food.extras),
        totalCost: new FormControl(food.totalCost),
        foodQuantityTagId: new FormControl(food.foodQuantityTagId)
      })
      this.cartRequestDetails.push(cartRequestDetails)
    })
  }

  calculateTotalAmount() {
    this.totalAmount = this.data.cartFoods.reduce((acc, curr) => acc + (curr.price * curr.quantity), 0)
    this.serviceChargeableTotalAmount = this.data.cartFoods
      .filter(x => x.isServiceCharge || x.isServiceCharge == null).reduce((acc, curr) => acc + (curr.price * curr.quantity), 0)
  }

  updateCalculations() {
    this.calculateGrandTotal();
    const paidAmount = this.billForm.get('orderBillRequest.paidAmount')?.value || 0;
    this.balance = paidAmount - this.grandTotal;
    if (paidAmount > 0) {
      this.billForm.get('orderBillRequest.balance')?.setValue(+parseFloat(String(this.balance)).toFixed(2));
    }
  }

  private calculateGrandTotal() {
    const discount = this.billForm.get('orderBillRequest.discountDetail.discountPercentage')?.value || 0;
    const cardTax = this.billForm.get('orderBillRequest.cardTax')?.value || 0;
    this.discount = (this.totalAmount * discount) / 100;
    this.cardTax = (this.totalAmount * cardTax) / 100;
    let serviceChargeAmount = 0;
    let taxAmount = 0;
    if (this.serviceChargeChecked) {
      serviceChargeAmount = this.serviceChargeChecked ? (this.serviceChargeableTotalAmount * this.billForm.get('orderBillRequest.serviceChargeDetail.serviceChargePercentage')?.value) / 100 : 0;
      this.billForm.get('orderBillRequest.serviceChargeDetail.serviceChargeAmount')?.setValue(serviceChargeAmount);
      this.billForm.get('orderBillRequest.serviceChargeDetail.amountAfterServiceCharge')?.setValue(this.totalAmount + serviceChargeAmount);
    }
    if (this.taxChecked) {
      taxAmount = (this.totalAmount * this.billForm.get('orderBillRequest.taxDetail.taxPercentage')?.value) / 100;
      this.billForm.get('orderBillRequest.taxDetail.taxAmount')?.setValue(taxAmount);
      this.billForm.get('orderBillRequest.taxDetail.amountAfterTax')?.setValue(this.totalAmount + taxAmount);
    }
    if (this.discountAdded) {
      this.billForm.get('orderBillRequest.discountDetail.discountAmount')?.setValue(this.discount);
      this.billForm.get('orderBillRequest.discountDetail.amountAfterDiscount')?.setValue(this.totalAmount - this.discount);
    }
    this.grandTotal = this.totalAmount - this.discount + serviceChargeAmount + taxAmount + this.cardTax;
    this.billForm.get('orderBillRequest.netAmount')?.setValue(this.grandTotal);
    this.serviceCharge = serviceChargeAmount;
    this.tax = taxAmount;
  }

  onCloseOrder() {
    if (this.billForm.valid) {
      this.submitForm()
    } else {
      // Recurses into the nested groups, and sets touched - which is what the
      // template error messages are keyed on.
      this.billForm.markAllAsTouched();
    }
  }

  private submitForm() {
    // Close Order is clickable again while the POST is in flight, so guard the double submit.
    if (this.submitting) {
      return;
    }
    // Read the value being sent, not the discountAdded flag - the flag goes stale when the
    // field is cleared and a null percentage fails to deserialise as a decimal server-side.
    if (!this.billForm.get('orderBillRequest.discountDetail.discountPercentage')?.value) {
      this.billForm.get('orderBillRequest.discountDetail.discountPercentage')?.setValue(0)
      this.billForm.get('orderBillRequest.discountDetail.discountAmount')?.setValue(0)
      this.billForm.get('orderBillRequest.discountDetail.amountAfterDiscount')?.setValue(0)
    }
    if (!this.serviceChargeChecked) {
      this.billForm.get('orderBillRequest.serviceChargeDetail.serviceChargePercentage')?.setValue(0)
      this.billForm.get('orderBillRequest.serviceChargeDetail.amountAfterServiceCharge')?.setValue(0)
      this.billForm.get('orderBillRequest.serviceChargeDetail.serviceChargeAmount')?.setValue(0)
    }
    if (!this.taxChecked) {
      this.billForm.get('orderBillRequest.taxDetail.taxPercentage')?.setValue(0)
      this.billForm.get('orderBillRequest.taxDetail.amountAfterTax')?.setValue(0)
      this.billForm.get('orderBillRequest.taxDetail.taxAmount')?.setValue(0)
    }

    // Counter service keeps the order open for the kitchen, so "closed" would be a lie.
    const successMessage = this.restaurantDetail?.restaurantDetail?.counterServiceEnabled
      ? 'Order placed — sent to kitchen'
      : 'Order has been closed successfully';

    this.submitting = true;
    this.apiBaseService.POST_API([ApiEnum.RestaurantModule, ApiEnum.POS], this.billForm.value,
      true, true, successMessage)
      .subscribe({
        next: (res) => {
          // Print only once the order is actually saved.
          this.printBill()
          this.dialogRef.close(true)
        },
        error: () => {
          // Error alert comes from the interceptor; just let the cashier retry.
          this.submitting = false;
        }
      })
  }

  private printBill() {
    const customPrintOptions: PrintOptions = new PrintOptions({
      printSectionId: 'print-section',
      useExistingCss: true,
      // Add any other print options as needed
    });
    this.printService.print(customPrintOptions)
  }

  // private generateBarcode() {
  //   JsBarcode('#barcode', this.data.onGoingOrder.orderNumber, {
  //     format: 'code128', // default
  //     height: 10 * this.px2mmFactor, // 10mm
  //     width: 2.3,
  //     // displayValue: false,
  //     text: this.data.onGoingOrder.orderNumber,
  //     background: 'transparent',
  //     font: 'monospace',
  //     fontOptions: 'normal',
  //     fontSize: 16,
  //     lineColor: 'black',
  //     margin: 5 * this.px2mmFactor, // 5mm
  //     textMargin: 2 * this.px2mmFactor, // 2mm
  //     // textAlign: 'right',
  //     // textPosition: 'top',
  //   });
  // }

  private calcPx2MmFactor() {
    let e = document.createElement('div');
    e.style.position = 'absolute';
    e.style.width = '100mm';
    document.body.appendChild(e);
    let rect = e.getBoundingClientRect();
    document.body.removeChild(e);
    return rect.width / 100;
  }

  onDiscountAdded() {
    this.discountAdded = this.billForm.get('orderBillRequest.discountDetail.discountPercentage')?.value > 0;
    // if (this.billForm.get('discountDetail.discountPercentage')?.value <= this.data.onGoingOrder.marginPercentage) {
    //   this.updateCalculations();
    // }
    // const discountDetail: DiscountDetails = {
    //   orderId: this.data.onGoingOrder.orderId,
    //   discountPercentage: this.billForm.get('discountDetail.discountPercentage')?.value
    // }

    // const discountDetails: DiscountDetails[] = this.storageService.getFromLocalStorage('discountDetails') || [];
    // const savedDiscountDetail = discountDetails.find(discount => discount.orderId === this.data.onGoingOrder.orderId)
    // if (savedDiscountDetail) {
    //   discountDetails.splice(discountDetails.indexOf(savedDiscountDetail), 1)
    // }
    // discountDetails.push(discountDetail)
    // this.storageService.saveToLocalStorage('discountDetails', discountDetails)

  }


  // getTaxAmount(): Observable<TaxValueResponse> {
  //   return this.apiBaseService.GET<TaxValueResponse>([ApiEnum.RestaurantModule, ApiEnum.SystemSetting, ApiEnum.TaxValue])
  // }
  //
  // getServiceChargeAmount(): Observable<ServiceChargeResponse> {
  //   return this.apiBaseService.GET<ServiceChargeResponse>([ApiEnum.RestaurantModule, ApiEnum.SystemSetting, ApiEnum.ServiceCharge])
  // }

  getRestaurantData(): Observable<RestaurantConfig> {
    return this.apiBaseService.GET<RestaurantConfig>([ApiEnum.RestaurantModule, ApiEnum.SystemSetting, ApiEnum.RestaurantDetail])
  }

  private getFoodItemQuantity(): void {
    this.apiBaseService.GET<FoodItemQuantity[]>([ApiEnum.FoodModule, ApiEnum.FoodItemQuantityTag])
      .subscribe(res => {
        this.foodItemQuantity = res
      });
  }
}
